// WorkspacesContextProvider.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthorization } from 'contexts/AuthContextProvider.jsx';
import { getCachedImageOrFetch } from '../cache/imageCache';
import { useImageCache } from 'contexts/ImageCacheProvider.jsx';

const WorkspacesContext = createContext();
export const useWorkspaces = () => useContext(WorkspacesContext);

export const WorkspacesProvider = ({ children }) => {
  const [workspaces, setWorkspaces] = useState([]);
  const [workspaceWorkers, setWorkspaceWorkers] = useState([]);
  const { accessToken, authAxios, getLoggedUser } = useAuthorization();
  const [loading, setLoading] = useState(true);
  const { cacheClearCount } = useImageCache();

  const fetchAndCacheWorkers = useCallback(async (workersData) => {
    return Promise.all(
      workersData.map(async (worker) => {
        const result = await getCachedImageOrFetch(worker.image, `worker-${worker.id}`);
        if (!result) return worker;
        return { ...worker, image: result.blobUrl ?? worker.image };
      })
    );
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!accessToken) return;
      try {
        const workspaceResponse = await authAxios({
          method: 'get',
          url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/`,
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        setWorkspaces(workspaceResponse.data);

        if (workspaceResponse.data.length !== 0) {
          const workspaceWorkersResponse = await authAxios({
            method: 'get',
            url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspaceResponse.data[0].id}/workers/?compress=true`,
            headers: { Authorization: `Bearer ${accessToken}` },
          });

          const updatedWorkers = await fetchAndCacheWorkers(workspaceWorkersResponse.data);
          setWorkspaceWorkers(updatedWorkers);
        }
      } catch (error) {
        console.error('Error fetching workspace data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line
  }, [accessToken, authAxios, cacheClearCount]);

  const getCurrentlyLoggedUser = () => {
    if (loading || workspaceWorkers.length === 0) return null;
    const loggedUserID = getLoggedUser();
    return workspaceWorkers.find((worker) => worker.id === loggedUserID) || null;
  };

  const getLoggedUserPermissions = () => {
    const currentUser = getCurrentlyLoggedUser();
    return currentUser
      ? {
          is_admin: currentUser.is_admin,
          is_owner: currentUser.is_owner,
          is_staff: currentUser.is_staff,
          user_id: currentUser.id,
        }
      : { is_admin: false, is_owner: false, is_staff: false };
  };

  const handleWorkspaceChange = async (id, load_index_page = true) => {
    await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${id}/workspaceAccess/`,
      { workspace: id },
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    if (load_index_page) window.location.href = "/workflow/index";
  };

    const refreshWorkspaceWorkers = async () => {
        if (!workspaces[0]?.id) return;
        try {
            const workspaceWorkersResponse = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspaces[0].id}/workers/?compress=true`,
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            setWorkspaceWorkers(workspaceWorkersResponse.data);
        } catch (error) {
            console.error('Error refreshing workspace workers:', error);
        }
    };

  return (
    <WorkspacesContext.Provider
      value={{
        getCurrentlyLoggedUser,
        getLoggedUserPermissions,
        workspaceWorkers,
        loading,
        workspaces,
        setWorkspaces,
        handleWorkspaceChange,
        refreshWorkspaceWorkers
      }}
    >
      {children}
    </WorkspacesContext.Provider>
  );
};